// eslint-disable-next-line import/no-restricted-paths
import { Guest } from "@CHILLNN-Inc/chillnn-driver-reservation-react";
import { memo, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { AuthWrapper } from "@/lib/aws-amplify";

import { usePayjpClient } from "@/hooks/use-payjp-client";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";

type Props = {
	hotelID: string;
	reservationID: string;
	isHostDirectReservationAvailable: boolean;
};

const uri = import.meta.env.VITE_GRAPHQL_API_ENDPOINT_RESERVATION_HOST_DIRECT;

export const ReplicateReservationButton = memo(
	({ hotelID, reservationID, isHostDirectReservationAvailable }: Props) => {
		const [isOpen, setIsOpen] = useState(false);
		const onOpen = useCallback(() => {
			setIsOpen(true);
		}, []);
		const onClose = useCallback(() => {
			setIsOpen(false);
		}, []);

		const handleClick = useCallback(() => {
			onOpen();
		}, [onOpen]);

		const navigate = useNavigate();
		const { payjp } = usePayjpClient();

		const initializeContext = useMemo<Guest.ReservationComponent.Full.Props["initializeContext"]>(() => {
			return {
				sessionStateContext: {
					hotelID,
					reservationIDOfReplicationSource: reservationID,
					lang: Guest.LangCode.Default,
				},
			};
		}, [hotelID, reservationID]);

		const callbackContext = useMemo<Guest.ReservationComponent.Full.Props["callbackContext"]>(() => {
			return {
				onSuccessCallback: ({ hotelID, reservationID }) => {
					onClose();

					notifyUserEventInfo({
						variant: "default",
						title: "新規予約が完了しました",
					});

					const reservationDetailPagePath = generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
						hotelID,
						reservationID,
					});
					navigate(reservationDetailPagePath, { replace: true });
				},
				onHideCallback: () => {
					onClose();
				},
				onGetPayjpBackUrlCallback: () => () => "",
			};
		}, [navigate, onClose]);

		const libContext = useMemo<Guest.ReservationComponent.Full.Props["libContext"]>(() => {
			return {
				apolloClient: {
					uri,
					getToken: async () => (await AuthWrapper.getIdToken()) ?? "",
				},
				payjp: {
					instance: payjp!,
				},
			};
		}, [payjp]);

		const envContext = useMemo<Guest.ReservationComponent.Full.Props["envContext"]>(() => {
			return {
				stage: import.meta.env.VITE_STAGE,
				user: "host",
			};
		}, []);

		const canReplicate = useMemo(() => {
			return isHostDirectReservationAvailable;
		}, [isHostDirectReservationAvailable]);

		const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
			if (canReplicate) {
				return "outline";
			}

			return "disabled";
		}, [canReplicate]);

		const buttonTooltip = useMemo(() => {
			if (!canReplicate) {
				return "施設からの直接予約は現地決済限定です。ホテルポリシー設定/現地決済 の設定をしてください";
			}

			return undefined;
		}, [canReplicate]);

		return (
			<>
				<Button variant={buttonVariant} type="button" size="sm" onClick={handleClick} tooltip={buttonTooltip}>
					同様の顧客情報で予約を作成
				</Button>

				{isOpen && (
					<Guest.ReservationComponent.Full.Presenter
						initializeContext={initializeContext}
						callbackContext={callbackContext}
						libContext={libContext}
						envContext={envContext}
					/>
				)}
			</>
		);
	},
);
